import { useSyncExternalStore } from 'react';
import { container } from '@/app/container';
import { ContentRepositoryToken } from '../di/tokens';
import type { ContentEditLockResult } from '../domain/entities/content.entity';

/**
 * O lock de edição do content, do lado da tela: **um registro só para a aba**
 * (decisão 10 do épico content-admin), e não um `setInterval` por tela.
 *
 * O back trava por perfil (`POST admin/content/edit-locks`, que é aquisição e
 * heartbeat ao mesmo tempo) e solta um lock 10 min depois do último sinal.
 * Aqui mora o `Set` dos `contentIds` que esta aba está editando e o timer que
 * manda todos eles num `POST` só a cada `HEARTBEAT_MS`. Toda entrada em modo
 * edição chama `addContentLock`, e toda saída chama `removeContentLock`.
 *
 * Fechar a aba não manda nada, de propósito: o lock vence sozinho.
 *
 * Fica fora do React porque o sinal é da aba, não de um componente: duas telas
 * abertas ao mesmo tempo dividem o mesmo timer. Os componentes leem o estado
 * por `useContentLockState`.
 */

export const HEARTBEAT_MS = 60_000;

export interface ContentLockOwner {
  platformProfileId: string;
  /** Ausente quando o perfil não resolve: a tela diz "outra pessoa". */
  name?: string;
  since: string;
}

/**
 * - `held`: o lock é desta aba, e a tela está em modo edição.
 * - `locked`: outra pessoa já estava editando quando se clicou em editar.
 * - `lost`: o lock era desta aba e outra pessoa o pegou — o perfil ficou mais
 *   de 10 min sem sinal (laptop dormindo). O form continua montado para a
 *   pessoa copiar o texto, mas não salva mais.
 * - `error`: a aquisição falhou (rede, 5xx, conteúdo que não existe mais).
 */
export type ContentLockState =
  | { status: 'held' }
  | { status: 'locked'; lockedBy?: ContentLockOwner }
  | { status: 'lost'; lockedBy?: ContentLockOwner }
  | { status: 'error'; error: unknown };

export interface ContentLockApi {
  acquireEditLocks(contentIds: string[]): Promise<ContentEditLockResult[]>;
  releaseEditLocks(contentIds: string[]): Promise<void>;
}

/** Fábrica para o teste ter um registro limpo por caso; o app usa o de baixo. */
export function createContentLockRegistry(api: () => ContentLockApi) {
  const lockedContentIds = new Set<string>();
  const states = new Map<string, ContentLockState>();
  const listeners = new Set<() => void>();
  let heartbeat: ReturnType<typeof setTimeout> | undefined;

  const setState = (contentId: string, state: ContentLockState | undefined) => {
    if (state) states.set(contentId, state);
    else states.delete(contentId);
    for (const listener of listeners) listener();
  };

  const schedule = () => {
    clearTimeout(heartbeat);
    heartbeat =
      lockedContentIds.size > 0
        ? setTimeout(() => void syncContentLock(), HEARTBEAT_MS)
        : undefined;
  };

  const apply = (result: ContentEditLockResult) => {
    const { contentId } = result;
    if (!lockedContentIds.has(contentId)) {
      // Saiu da edição com o `POST` no ar: o servidor pode ter processado o
      // `DELETE` antes dele e deixado o lock de pé por 10 min. Solta de novo.
      if (result.status === 'ACQUIRED') release(contentId);
      return;
    }
    if (result.status === 'ACQUIRED') {
      setState(contentId, { status: 'held' });
      return;
    }
    lockedContentIds.delete(contentId);
    if (result.status === 'NOT_FOUND') {
      setState(contentId, {
        status: 'error',
        error: 'Este conteúdo não existe mais.',
      });
      return;
    }
    // Era desta aba e voltou de outro: perdido. Nunca foi: travado por outro.
    setState(contentId, {
      status: states.get(contentId)?.status === 'held' ? 'lost' : 'locked',
      lockedBy: result.lockedBy,
    });
  };

  /**
   * Um `POST` com todos os ids do `Set`, e o timer reagendado. Falha de rede
   * no heartbeat não é perda: ninguém muda de estado e o próximo ciclo tenta
   * de novo — o vencimento de 10 min dá folga para isso. Só quem estava sendo
   * adquirido agora (`acquiring`) vira `error`, porque esse ainda não editava.
   *
   * Nada daqui vai para o `console`: a resposta leva o nome de quem edita.
   */
  async function syncContentLock(acquiring?: string): Promise<void> {
    const ids = [...lockedContentIds];
    if (ids.length === 0) {
      schedule();
      return;
    }
    try {
      const results = await api().acquireEditLocks(ids);
      for (const result of results) apply(result);
    } catch (error) {
      if (acquiring !== undefined && lockedContentIds.delete(acquiring)) {
        setState(acquiring, { status: 'error', error });
      }
    } finally {
      schedule();
    }
  }

  // O lock vence sozinho em 10 min: um `DELETE` que falha não deixa nada
  // preso por mais tempo que isso, e não há o que a tela possa fazer.
  const release = (contentId: string) => {
    void api()
      .releaseEditLocks([contentId])
      .catch(() => undefined);
  };

  /**
   * Entra em edição. Sincroniza na hora — a aquisição de um id também renova
   * os outros e reinicia a contagem — e devolve o estado do id.
   */
  async function addContentLock(contentId: string): Promise<ContentLockState> {
    lockedContentIds.add(contentId);
    await syncContentLock(contentId);
    return (
      states.get(contentId) ?? {
        status: 'error',
        error: 'O servidor não respondeu sobre este conteúdo.',
      }
    );
  }

  /**
   * Sai da edição: concluir, cancelar, desmontar a tela. Limpa também o
   * `locked`/`lost`/`error` do id, e só manda o `DELETE` do que era desta aba.
   */
  function removeContentLock(contentId: string): void {
    const held = lockedContentIds.delete(contentId);
    setState(contentId, undefined);
    if (lockedContentIds.size === 0) schedule();
    if (held) release(contentId);
  }

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  const getState = (contentId: string | undefined) =>
    contentId === undefined ? undefined : states.get(contentId);

  return {
    addContentLock,
    removeContentLock,
    syncContentLock,
    subscribe,
    getState,
  };
}

const registry = createContentLockRegistry(() =>
  container.resolve(ContentRepositoryToken),
);

export const addContentLock = registry.addContentLock;
export const removeContentLock = registry.removeContentLock;

/** Estado do lock de um id, ou `undefined` quando ninguém pediu por ele. */
export function useContentLockState(
  contentId: string | undefined,
): ContentLockState | undefined {
  return useSyncExternalStore(registry.subscribe, () =>
    registry.getState(contentId),
  );
}
